import React, { useEffect, useState, useCallback } from 'react';
import { supabaseAdmin } from '../lib/supabase';
import { supabaseService } from '../lib/supabaseService';
import { logAction, getCurrentAdmin } from '../services/logger';
import { Bell, Send, Smartphone, Clock, AlertTriangle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';

type Mode = 'daily' | 'teams' | 'all';

interface Stats {
  activeTokens: number;
  enabledNotifs: number;
  iosCount: number;
  androidCount: number;
  lastSendAt: string | null;
  lastSendBy: string | null;
}

interface HistoryLog {
  id: string;
  admin_email: string;
  action: string;
  description: string;
  new_value: any;
  created_at: string;
}

const MODE_LABELS: Record<Mode, string> = {
  daily: 'Ce soir à Lille (daily)',
  teams: 'Équipes favorites (teams)',
  all: 'Les deux (all)',
};

const GITHUB_REPO = 'LucasCTSW/onpitch';
const GITHUB_WORKFLOW = 'notifications.yml';
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN || '';

export function NotificationsPage() {
  const [stats, setStats] = useState<Stats>({
    activeTokens: 0,
    enabledNotifs: 0,
    iosCount: 0,
    androidCount: 0,
    lastSendAt: null,
    lastSendBy: null,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [mode, setMode] = useState<Mode>('daily');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const [tokensRes, enabledRes, platformsRes, lastSendRes] = await Promise.all([
        supabaseService
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .not('push_token', 'is', null),
        supabaseService
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .eq('notifications_enabled', true)
          .not('push_token', 'is', null),
        supabaseService.from('push_tokens').select('platform'),
        supabaseAdmin
          .from('admin_logs')
          .select('created_at, admin_email')
          .eq('action', 'send_notification')
          .order('created_at', { ascending: false })
          .limit(1),
      ]);

      const platformRows = (platformsRes.data || []) as Array<{ platform: string | null }>;
      const iosCount = platformRows.filter(r => r.platform === 'ios').length;
      const androidCount = platformRows.filter(r => r.platform === 'android').length;

      const lastLog = (lastSendRes.data || [])[0];

      setStats({
        activeTokens: tokensRes.count || 0,
        enabledNotifs: enabledRes.count || 0,
        iosCount,
        androidCount,
        lastSendAt: lastLog?.created_at || null,
        lastSendBy: lastLog?.admin_email || null,
      });
    } catch (err: any) {
      console.error('Load notification stats error:', err);
      setStatsError(err?.message || 'Erreur de chargement');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data, error } = await supabaseAdmin
        .from('admin_logs')
        .select('id, admin_email, action, description, new_value, created_at')
        .eq('action', 'send_notification')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      setHistory((data || []) as HistoryLog[]);
    } catch (err) {
      console.error('Load notification history error:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadHistory();
  }, [loadStats, loadHistory]);

  const handleSend = async () => {
    if (!GITHUB_TOKEN) {
      toast.error('Configurez REACT_APP_GITHUB_TOKEN dans admin/.env');
      return;
    }
    const trimmedMessage = customMessage.trim();
    const modeLabel = MODE_LABELS[mode];

    if (!window.confirm(
      `Envoyer une notification — ${modeLabel}${trimmedMessage ? `\n\nMessage : « ${trimmedMessage} »` : ''}`
    )) return;

    setSending(true);
    try {
      const res = await fetch(
        `https://api.github.com/repos/${GITHUB_REPO}/actions/workflows/${GITHUB_WORKFLOW}/dispatches`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${GITHUB_TOKEN}`,
            Accept: 'application/vnd.github+json',
            'X-GitHub-Api-Version': '2022-11-28',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ref: 'main',
            inputs: {
              mode,
              custom_message: trimmedMessage,
            },
          }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`GitHub API ${res.status} — ${text || res.statusText}`);
      }

      const adminData = getCurrentAdmin();
      await logAction(
        'send_notification',
        'notification',
        null,
        null,
        { mode, custom_message: trimmedMessage || null },
        `${adminData?.name || 'Admin'} a déclenché une notification ${modeLabel}${trimmedMessage ? ` — « ${trimmedMessage} »` : ''}`
      );

      toast.success('Workflow déclenché — les notifications partent dans quelques secondes');
      setCustomMessage('');
      // Refresh stats + history shortly after (workflow takes a few sec to log)
      setTimeout(() => { loadStats(); loadHistory(); }, 2500);
    } catch (err: any) {
      console.error('Send notification error:', err);
      toast.error(err?.message || 'Erreur GitHub API');
    } finally {
      setSending(false);
    }
  };

  const lastSendLabel = stats.lastSendAt
    ? formatDistanceToNow(new Date(stats.lastSendAt), { addSuffix: true, locale: fr })
    : '—';

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Bell size={20} style={{ color: 'var(--accent)' }} />
        Notifications push
      </h2>

      {/* ── Stats ── */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Tokens actifs</div>
              <div className="stat-value">{statsLoading ? '…' : stats.activeTokens}</div>
              <div className="stat-sub">profils avec push_token</div>
            </div>
            <Smartphone size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Notifs activées</div>
              <div className="stat-value" style={{ color: 'var(--green)' }}>
                {statsLoading ? '…' : stats.enabledNotifs}
              </div>
              <div className="stat-sub">prêts à recevoir</div>
            </div>
            <Bell size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div className="stat-label">Dernier envoi</div>
              <div className="stat-value" style={{ fontSize: stats.lastSendAt ? '18px' : '26px' }}>
                {statsLoading ? '…' : lastSendLabel}
              </div>
              <div className="stat-sub">
                {stats.lastSendBy ? `par ${stats.lastSendBy.split('@')[0]}` : 'aucun envoi manuel'}
              </div>
            </div>
            <Clock size={18} style={{ color: 'var(--text-dim)' }} />
          </div>
        </div>
      </div>

      {statsError && (
        <div className="card" style={{ marginBottom: '16px', borderColor: 'var(--red-dim)', background: 'var(--red-dim)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--red)', fontSize: '13px' }}>
            <AlertTriangle size={16} />
            Impossible de charger les stats : {statsError}
          </div>
        </div>
      )}

      {/* ── Send + Platforms ── */}
      <div className="dashboard-2col" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div className="card">
          <h3 className="card-title">Envoyer une notification</h3>

          {!GITHUB_TOKEN && (
            <div style={{
              padding: '10px 12px',
              marginBottom: '14px',
              background: 'var(--orange-dim)',
              border: '1px solid rgba(198,122,0,0.25)',
              borderRadius: '8px',
              color: 'var(--orange)',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <AlertTriangle size={14} />
              Configurez <code style={{ fontFamily: 'monospace' }}>REACT_APP_GITHUB_TOKEN</code> dans <code style={{ fontFamily: 'monospace' }}>admin/.env</code> puis rebuild.
            </div>
          )}

          <div className="form-field">
            <label>Mode</label>
            <select
              className="filter-select"
              style={{ width: '100%' }}
              value={mode}
              onChange={e => setMode(e.target.value as Mode)}
              disabled={sending}
            >
              <option value="daily">{MODE_LABELS.daily}</option>
              <option value="teams">{MODE_LABELS.teams}</option>
              <option value="all">{MODE_LABELS.all}</option>
            </select>
          </div>

          <div className="form-field">
            <label>Message custom (optionnel)</label>
            <textarea
              rows={3}
              placeholder="Laisse vide pour garder le message par défaut du script"
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
              disabled={sending}
              style={{ fontSize: '13px', resize: 'vertical' }}
            />
            <div style={{ fontSize: '11px', color: 'var(--text-dim)', marginTop: '4px' }}>
              Si rempli, remplace le body des notifications envoyées dans ce run (titre inchangé).
            </div>
          </div>

          <button
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending || !GITHUB_TOKEN}
          >
            <Send size={14} />
            {sending ? 'Envoi…' : 'Envoyer maintenant'}
          </button>
        </div>

        <div className="card">
          <h3 className="card-title">Tokens par plateforme</h3>
          {statsLoading ? (
            <p className="muted">Chargement…</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>iOS</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--accent)' }}>{stats.iosCount}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-primary)', borderRadius: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>Android</span>
                <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--accent)' }}>{stats.androidCount}</span>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-dim)', textAlign: 'center', marginTop: '4px' }}>
                Source : table <code>push_tokens</code>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── History ── */}
      <div className="card">
        <h3 className="card-title">Historique des envois manuels</h3>
        {historyLoading ? (
          <p className="muted">Chargement…</p>
        ) : history.length === 0 ? (
          <p className="muted">Aucun envoi manuel enregistré</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Mode</th>
                <th>Description</th>
                <th>Admin</th>
              </tr>
            </thead>
            <tbody>
              {history.map(log => {
                const modeValue = (log.new_value && log.new_value.mode) || '—';
                const date = format(new Date(log.created_at), 'dd/MM HH\'h\'mm', { locale: fr });
                return (
                  <tr key={log.id}>
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{date}</td>
                    <td>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: 700,
                        color: 'var(--accent)',
                        background: 'var(--accent-dim)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}>
                        {modeValue}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px' }}>{log.description || '—'}</td>
                    <td style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      {log.admin_email.split('@')[0]}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
